import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { Tables } from "@/integrations/supabase/types"
import { useAuth } from "./useAuth"
import { toast } from "sonner"

type Announcement = Tables<"announcements">
type Profile = Tables<"profiles">

type AnnouncementWithAuthor = Announcement & {
  author: Profile | null
}

export const useAnnouncements = () => {
  const { profile } = useAuth()
  
  // Check if QueryClient is available
  try {
    const queryClient = useQueryClient()
  } catch {
    // Return mock data when QueryClient is not available
    return {
      data: [],
      isLoading: false,
      error: null
    }
  }
  
  return useQuery({
    queryKey: ["announcements", profile?.condominium_id],
    queryFn: async () => {
      if (!profile?.condominium_id) {
        console.log("Usuário sem condomínio associado")
        return []
      }

      console.log("Buscando avisos para usuário:", profile.id, "Condomínio:", profile.condominium_id)

      const now = new Date().toISOString()

      // Buscar avisos do condomínio (a RLS policy já filtra por target_audience e role)
      const { data, error } = await supabase
        .from("announcements")
        .select(`
          *,
          author:profiles!announcements_author_id_fkey(
            id,
            full_name,
            role,
            condominium_id
          )
        `)
        .or(`expires_at.is.null,expires_at.gte.${now}`)
        .order("created_at", { ascending: false })

      if (error) {
        console.error("Erro ao buscar avisos:", error)
        throw error
      }

      console.log("Avisos carregados para role:", profile.role, "Total:", data?.length)
      
      // Filtrar avisos do mesmo condomínio
      const filteredData = data?.filter(announcement => {
        return announcement.author?.condominium_id === profile.condominium_id
      }) || []

      console.log("Avisos filtrados por condomínio:", filteredData.length)
      return filteredData as AnnouncementWithAuthor[]
    },
    enabled: !!profile?.condominium_id
  })
}

export const useUpdateAnnouncement = () => {
  let queryClient
  try {
    queryClient = useQueryClient()
  } catch {
    // Return a mock mutation when QueryClient is not available
    return {
      mutate: () => {},
      mutateAsync: () => Promise.resolve(),
      isLoading: false,
      isPending: false,
      error: null
    }
  }

  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & Partial<Announcement>) => {
      const { data, error } = await supabase
        .from("announcements")
        .update(updates)
        .eq("id", id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcements"] })
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
      toast.success("Aviso atualizado com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao atualizar aviso:", error)
      toast.error("Erro ao atualizar aviso")
    }
  })
}

export const useDeleteAnnouncement = () => {
  let queryClient
  try {
    queryClient = useQueryClient()
  } catch {
    // Return a mock mutation when QueryClient is not available
    return {
      mutate: () => {},
      mutateAsync: () => Promise.resolve(),
      isLoading: false,
      isPending: false,
      error: null
    }
  }

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("announcements")
        .delete()
        .eq("id", id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcements"] })
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
      toast.success("Aviso excluído com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao excluir aviso:", error)
      toast.error("Erro ao excluir aviso")
    }
  })
}
